import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center bg-white overflow-hidden">
      {/* Background subtle grid */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(#1A1A1A 1px, transparent 1px), linear-gradient(90deg, #1A1A1A 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Decorative large circle - right */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45vw] h-[45vw] max-w-[700px] max-h-[700px] rounded-full bg-[#F5F5F3] -mr-20 hidden lg:block" />

      {/* Abstract image top-right */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 w-[38vw] max-w-[560px] hidden lg:block">
        <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/d8ba58a3fe8ef550bb916d8b10ad1060.jpg"
            alt="WABICH design studio"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-white/10" />
        </div>
        {/* Small accent label */}
        <div className="mt-4 flex items-center gap-2">
          <div className="w-8 h-px bg-gray-300" />
          <span className="text-xs text-gray-400 tracking-widest uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            Lille, France
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-24 pb-16">
        <div className="lg:max-w-[52%]">
          {/* Eyebrow */}
          <div
            className={`flex items-center gap-3 mb-8 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-8 h-px bg-gray-800" />
            <span
              className="text-xs tracking-widest uppercase text-gray-500"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
            >
              Studio de design créatif
            </span>
          </div>

          {/* Main title */}
          <h1
            className={`transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontWeight: 300,
              fontSize: 'clamp(48px, 7vw, 96px)',
              lineHeight: '1.05',
              color: '#1A1A1A',
              letterSpacing: '-0.02em',
            }}
          >
            Studio de<br />
            <em style={{ fontStyle: 'italic', fontWeight: 300 }}>design</em><br />
            créatif
          </h1>

          {/* Subtitle */}
          <p
            className={`mt-8 max-w-md text-base md:text-lg text-gray-500 leading-relaxed transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            WABICH propose des services de design pour les entreprises et les particuliers.
            Créer des visuels modernes, clairs et adaptés aux besoins de chaque client.
          </p>

          {/* CTA buttons */}
          <div
            className={`mt-10 flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1A1A1A] text-white text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-[#333] transition-colors duration-200 rounded-sm"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
            >
              Discuter d&apos;un projet
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <Link
              to="/a-propos"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gray-200 text-gray-700 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer hover:border-gray-900 hover:text-gray-900 transition-colors duration-200 rounded-sm"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
            >
              À propos
            </Link>
          </div>

          {/* Stats row */}
          <div
            className={`mt-16 flex flex-wrap gap-8 transition-all duration-700 delay-400 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {[
              { label: 'Design graphique', icon: 'ri-palette-line' },
              { label: 'Identité visuelle', icon: 'ri-brush-line' },
              { label: 'Conception créative', icon: 'ri-lightbulb-line' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-gray-400 text-base`} />
                </div>
                <span
                  className="text-xs text-gray-400 tracking-wide"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-px h-8 bg-gray-300" />
        <i className="ri-arrow-down-line text-gray-300 text-sm" />
      </div>
    </section>
  );
}
